import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { ChevronLeft, Check, MessageCircle } from 'lucide-react';
import './RefundPage.css';
import { WA_CS_LINK } from '../config/whatsapp';
import useSimRefund from './useSimRefund';
import { customerStatus, formatDateTime, formatRupiah } from './refundApi';
import { useLanguage } from '../i18n/LanguageContext';

// Brand names stay as they are; the generic methods translate
// (refund.payment.<type>).
const PAYMENT_LABELS = {
    qris: 'QRIS',
    gopay: 'GoPay',
    shopeepay: 'ShopeePay',
};
const TRANSLATED_PAYMENT_TYPES = ['credit_card', 'bank_transfer'];

// One row per step the customer can follow. CS-internal failures read as
// "still processing" here; CS resolves them from the admin page.
const buildSteps = (refund, t, locale) => {
    const { status } = refund;
    const reviewed = status !== 'PENDING_REVIEW';
    const cancelled = Boolean(refund.esimCancelledAt) || status === 'REFUNDED';
    const refunded = status === 'REFUNDED';
    const reviewedAt = (refund.events || []).find((e) => e.status === 'APPROVED')?.at;

    const steps = [
        { label: t('refund.statusPage.steps.received'), sub: formatDateTime(refund.createdAt, locale), done: true },
        {
            label: t('refund.statusPage.steps.reviewed'),
            sub: reviewed
                ? formatDateTime(reviewedAt, locale)
                : t('refund.statusPage.steps.reviewSub', { sla: t('refund.slaLabel') }),
            done: reviewed,
            current: !reviewed,
        },
        {
            label: t('refund.statusPage.steps.deactivated'),
            sub: cancelled ? formatDateTime(refund.esimCancelledAt, locale) : '',
            done: cancelled,
            current: reviewed && !cancelled,
        },
        {
            label: t('refund.statusPage.steps.refunded'),
            sub: refunded ? formatDateTime(refund.refundedAt, locale) : t('refund.statusPage.steps.refundedSub'),
            done: refunded,
            current: cancelled && !refunded,
        },
    ];

    return steps;
};

const RefundStatusPage = () => {
    const { SimId } = useParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
    const refundState = useSimRefund(SimId);
    const { t, locale } = useLanguage();

    useEffect(() => {
        if (!isLoading && !isAuthenticated) {
            localStorage.setItem('redirectAfterLogin', pathname);
            loginWithRedirect().catch((e) => console.error('Login error:', e));
        }
    }, [isLoading, isAuthenticated, loginWithRedirect, pathname]);

    const { refund } = refundState;
    const view = refund ? customerStatus(refund.status) : null;
    const paymentLabel = refund && (TRANSLATED_PAYMENT_TYPES.includes(refund.paymentType)
        ? t(`refund.payment.${refund.paymentType}`)
        : PAYMENT_LABELS[refund.paymentType]);

    let content;
    if (refundState.status === 'loading' || refundState.status === 'anonymous') {
        content = <div className="rf-state">{t('refund.statusPage.loading')}</div>;
    } else if (refundState.status === 'forbidden') {
        content = <div className="rf-state">{t('refund.statusPage.forbidden')}</div>;
    } else if (refundState.status === 'error') {
        content = (
            <div className="rf-state">
                {t('refund.statusPage.loadError')}
                <button type="button" className="rf-link-btn" onClick={refundState.reload}>{t('refund.statusPage.retry')}</button>
            </div>
        );
    } else if (!refund) {
        content = (
            <div className="rf-state">
                {t('refund.statusPage.none')}
                <Link className="rf-link-btn" to={`/sim/${SimId}/refund`}>{t('refund.statusPage.request')}</Link>
            </div>
        );
    } else {
        const steps = refund.status === 'REJECTED'
            ? [
                { label: t('refund.statusPage.steps.received'), sub: formatDateTime(refund.createdAt, locale), done: true },
                { label: t('refund.statusPage.steps.rejected'), sub: refund.rejectReason || '', done: true, rejected: true },
            ]
            : buildSteps(refund, t, locale);

        content = (
            <>
                <div className="rf-card rf-summary">
                    <span className={`rf-chip ${view.tone}`}>{t(`refund.status.${view.key}`)}</span>
                    <strong className="rf-amount">{formatRupiah(refund.amount)}</strong>
                    <p className="rf-muted">
                        {t('refund.statusPage.toOriginal')}{paymentLabel ? ` (${paymentLabel})` : ''} · {refund.refundId}
                    </p>
                </div>

                <ol className="rf-card rf-timeline">
                    {steps.map((step, i) => (
                        <li
                            key={i}
                            className={`rf-step ${step.done ? 'done' : ''} ${step.current ? 'current' : ''} ${step.rejected ? 'rejected' : ''}`}
                        >
                            <span className="rf-step-rail" aria-hidden="true">
                                <span className="rf-step-dot">{step.done && !step.rejected && <Check size={13} strokeWidth={3} />}</span>
                                {i < steps.length - 1 && <span className="rf-step-line" />}
                            </span>
                            <span className="rf-step-text">
                                <strong>{step.label}</strong>
                                {step.sub && <span>{step.sub}</span>}
                            </span>
                        </li>
                    ))}
                </ol>
            </>
        );
    }

    return (
        <div className="rf-page">
            <header className="rf-header">
                <button className="rf-icon-btn" onClick={() => navigate(`/sim/${SimId}`)} aria-label={t('common.back')}>
                    <ChevronLeft size={22} />
                </button>
                <h1>{t('refund.statusPage.title')}</h1>
            </header>

            <main className="rf-body">{content}</main>

            <footer className="rf-footer">
                <a className="rf-secondary" href={WA_CS_LINK} target="_blank" rel="noopener noreferrer">
                    <MessageCircle size={20} /> {t('refund.statusPage.askCs')}
                </a>
            </footer>
        </div>
    );
};

export default RefundStatusPage;
